import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * EmbraceMasthead — Round-4 editorial masthead + client cover band that
 * sits at the very top of the engagement's Embrace tab.
 *
 * Composition (top → bottom, single container, hairline-only rhythm):
 *
 *   1. Masthead line   ─ "Yadid Embrace" (Playfair, italic on the second
 *                        word) + "The strategic reflection instrument"
 *                        tagline + dateline with current-emphasis pill
 *                        and last-reflection whisper.
 *   2. Double hairline ─ printed-masthead separator.
 *   3. Cover band      ─ two columns:
 *                          left · client monogram + name + meta
 *                          right · optional atmospheric image with
 *                                  framing-question caption, OR a
 *                                  Yadid-texture fallback when no image
 *                                  is uploaded on the client record.
 *
 * All behaviour, data, and permissions from Phases A–C are untouched.
 * This is a bounded visual/composition pass — the masthead reads the
 * canonical roadmap + client fields; it never writes.
 *
 * Props:
 *   engagement   { id, name, engagement_type?, stage?, partner_lead? }
 *   clientId     string — fetches the client on mount to keep the parent
 *                contract unchanged.
 *   roadmap      the engagement roadmap object (for current_phase + last
 *                reflection timestamp).
 */
export default function EmbraceMasthead({ engagement, clientId, roadmap }) {
  const [client, setClient] = useState(null);
  // Feb 2026 · Iter 56 — Optional per-engagement Headline Image.
  // The masthead accepts the engagement prop from its parent but the
  // upload/remove flow is fully local so no callback threading is
  // required through EngagementEmbraceTab → EngagementDetail. On mount
  // we seed from `engagement.hero_image_url`; subsequent uploads
  // overwrite this local override until the next page load, when the
  // DB value re-hydrates naturally through props.
  const [engagementHero, setEngagementHero] = useState(engagement?.hero_image_url || null);
  // Iter 64 · Advisor-chosen framing for the headline image.
  // `heroPos` mirrors CSS `object-position: X% Y%` — 50/50 is the
  // default center crop. During drag-to-reposition we update the state
  // live for the WYSIWYG feel and PATCH the server on Save.
  const defaultPos = engagement?.hero_image_position || { x: 50, y: 50 };
  const [heroPos, setHeroPos] = useState(defaultPos);
  const [repositioning, setRepositioning] = useState(false);
  const [savingPos, setSavingPos] = useState(false);
  // Persisted framing snapshot — the value the SERVER currently has.
  // `cancelReposition` reverts to this instead of the (possibly stale)
  // engagement prop so cancelling after a mid-session save still snaps
  // back correctly.
  const savedPosRef = useRef(defaultPos);
  const dragOriginRef = useRef(null);   // { pointerId, startX, startY, posX, posY }
  const frameRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => {
    setEngagementHero(engagement?.hero_image_url || null);
    const pos = engagement?.hero_image_position || { x: 50, y: 50 };
    setHeroPos(pos);
    savedPosRef.current = pos;
  }, [engagement?.id, engagement?.hero_image_url, engagement?.hero_image_position?.x, engagement?.hero_image_position?.y]);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    api.get(`/clients/${clientId}`).then((r) => { if (alive) setClient(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [clientId]);

  const monogram = (client?.name || engagement?.name || "· ·")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const currentEmphasis = roadmap?.current_phase
    ? roadmap.current_phase.charAt(0).toUpperCase() + roadmap.current_phase.slice(1)
    : null;

  const lastReflectionAt = (() => {
    const phases = roadmap?.phases || [];
    let latest = null;
    for (const p of phases) {
      const stamps = [p.where_we_are_now_updated_at, ...(p.reflections || []).map((r) => r.created_at)].filter(Boolean);
      for (const s of stamps) {
        if (!latest || new Date(s) > new Date(latest)) latest = s;
      }
    }
    return latest;
  })();

  const relativeDays = (iso) => {
    if (!iso) return null;
    const days = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  const framingQuestion = (() => {
    const key = roadmap?.current_phase;
    if (!key) return "What is this engagement holding right now?";
    const p = (roadmap?.phases || []).find((x) => x.key === key);
    return p?.intent || "What is this engagement holding right now?";
  })();

  // Fallback hierarchy (Feb 2026 · Iter 56):
  //   1. Engagement headline image (explicitly set on THIS engagement)
  //   2. Existing Yadid atmospheric hero treatment
  //      (client atmosphere/hero → Yadid texture gradient)
  // Client imagery is not auto-inherited without an explicit engagement-
  // level decision — matches the product requirement.
  const hero = engagementHero || client?.hero_image_url || client?.atmosphere_image_url || null;
  const heroIsEngagement = Boolean(engagementHero);

  const uploadHeadline = async (file) => {
    if (!file || !engagement?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/engagements/${engagement.id}/hero-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEngagementHero(data.hero_image_url);
      toast.success("Headline image updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const removeHeadline = async () => {
    if (!engagement?.id) return;
    try {
      await api.delete(`/engagements/${engagement.id}/hero-image`);
      setEngagementHero(null);
      setHeroPos({ x: 50, y: 50 });
      setRepositioning(false);
      toast.success("Headline image removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  // ── Drag-to-reposition · updates CSS object-position live ──
  // A drag across the full frame maps 1:1 to a 100-point shift in
  // `object-position`; dragging the image DOWN reveals more of the
  // top of the source (y decreases). Both axes are clamped to [0, 100].
  // Uses pointer events so mouse, touch, and pen all work uniformly.
  const onFramePointerDown = (ev) => {
    if (!repositioning) return;
    const frame = frameRef.current;
    if (!frame) return;
    // Don't hijack pointerdown events that originate on the action
    // buttons (Save / Cancel / Drag-hint label). preventDefault-ing
    // them cancels the ensuing click and the buttons stop responding.
    if (ev.target?.closest?.('[data-testid="embrace-cover-headline-actions"]')) return;
    ev.preventDefault();
    frame.setPointerCapture?.(ev.pointerId);
    dragOriginRef.current = {
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      posX: heroPos.x,
      posY: heroPos.y,
    };
  };
  const onFramePointerMove = (ev) => {
    const origin = dragOriginRef.current;
    if (!origin || origin.pointerId !== ev.pointerId) return;
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    const dx = ev.clientX - origin.startX;
    const dy = ev.clientY - origin.startY;
    const nextX = Math.max(0, Math.min(100, origin.posX - (dx / rect.width) * 100));
    const nextY = Math.max(0, Math.min(100, origin.posY - (dy / rect.height) * 100));
    setHeroPos({ x: nextX, y: nextY });
  };
  const onFramePointerUp = (ev) => {
    if (dragOriginRef.current?.pointerId === ev.pointerId) {
      dragOriginRef.current = null;
      frameRef.current?.releasePointerCapture?.(ev.pointerId);
    }
  };

  const saveReposition = async () => {
    if (!engagement?.id) return;
    setSavingPos(true);
    try {
      await api.patch(`/engagements/${engagement.id}/hero-image/position`, {
        x: heroPos.x,
        y: heroPos.y,
      });
      savedPosRef.current = heroPos;
      setRepositioning(false);
      toast.success("Framing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPos(false);
    }
  };
  const cancelReposition = () => {
    setHeroPos(savedPosRef.current);
    setRepositioning(false);
  };

  const formatEngagementType = (t) => {
    if (!t) return "";
    if (t === "executive_communication") return "Executive Communication & Facilitation";
    return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const meta = [
    engagement?.name,
    formatEngagementType(engagement?.engagement_type),
    engagement?.partner_lead ? `Lead partner · ${engagement.partner_lead}` : null,
  ].filter(Boolean);

  // Iter 85.65 · Top-three relationship contacts.
  //   Prioritization (canonical Contact model at /app/backend/models.py):
  //     1. is_primary=true always leads
  //     2. influence=high before medium
  //     3. posture rank: champion → supportive → neutral → skeptical/blocker/unknown
  //     4. deterministic tiebreak by name so the strip doesn't shuffle
  //   Filters out low-influence contacts entirely so the strip only ever
  //   surfaces "strategically relevant" people, per spec.
  const topContacts = useMemo(() => {
    const list = (client?.stakeholders || []).filter(
      (s) => s && s.id && (s.influence === "high" || s.influence === "medium" || s.is_primary)
    );
    const POSTURE_RANK = { champion: 0, supportive: 1, neutral: 2, skeptical: 3, blocker: 4, unknown: 5 };
    const INFLUENCE_RANK = { high: 0, medium: 1, low: 2 };
    return [...list]
      .sort((a, b) => {
        if ((b.is_primary ? 1 : 0) !== (a.is_primary ? 1 : 0)) return (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);
        const ai = INFLUENCE_RANK[a.influence] ?? 3;
        const bi = INFLUENCE_RANK[b.influence] ?? 3;
        if (ai !== bi) return ai - bi;
        const ap = POSTURE_RANK[a.posture] ?? 5;
        const bp = POSTURE_RANK[b.posture] ?? 5;
        if (ap !== bp) return ap - bp;
        return (a.name || "").localeCompare(b.name || "");
      })
      .slice(0, 3);
  }, [client?.stakeholders]);

  const contactInitials = (name) =>
    (name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?";

  return (
    <header
      className="px-6 md:px-10 py-6 md:py-8 motion-reduce:transition-none"
      data-testid="embrace-masthead"
    >
      {/* ─── Masthead line ─────────────────────────────────────────── */}
      <div className="flex items-baseline justify-between gap-6 flex-wrap">
        <div
          className="tracking-tight text-foreground"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.65rem", lineHeight: 1.05, fontWeight: 900 }}
          data-testid="embrace-masthead-brand"
        >
          Yadid <em style={{ fontStyle: "italic", fontWeight: 400 }}>Embrace</em>
        </div>
        <div
          className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-muted-foreground text-right leading-relaxed"
          data-testid="embrace-masthead-dateline"
        >
          <div>
            {currentEmphasis ? (
              <>
                Current emphasis · <span className="text-brass">{currentEmphasis}</span>
              </>
            ) : (
              <span className="text-muted-foreground">Emphasis · not yet set</span>
            )}
          </div>
          {lastReflectionAt && (
            <div className="opacity-70">Last reflected {relativeDays(lastReflectionAt)}</div>
          )}
        </div>
      </div>

      {/* ─── Double hairline (printed masthead vocabulary) ────────── */}
      <div className="mt-4 border-t-2 border-double border-border/70" aria-hidden />

      {/* ─── Cover band ───────────────────────────────────────────── */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(220px,320px)_1fr] gap-6 md:gap-10 items-stretch">
        {/* Client card · left */}
        <div className="flex flex-col justify-between" data-testid="embrace-cover-client">
          <div>
            <div
              className="text-foreground select-none"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "3.4rem", lineHeight: 0.95, fontWeight: 900, letterSpacing: "-0.02em" }}
              data-testid="embrace-cover-monogram"
              aria-hidden
            >
              {monogram}
            </div>
            <div
              className="mt-2 text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.35rem", lineHeight: 1.15, fontWeight: 700 }}
            >
              {client?.name || "—"}
            </div>
            <div className="mt-2 text-[0.75rem] leading-relaxed text-muted-foreground" style={{ fontFamily: "'Merriweather', Georgia, serif" }}>
              {meta.map((m, i) => (
                <div key={i}>{m}</div>
              ))}
            </div>
            {/* Iter 85.65 · Top three relationship contacts.
                Restrained horizontal strip — initials chip + name + role,
                sorted by is_primary → high/medium influence → posture. Each
                chip deep-links into the existing Contact History drawer via
                the canonical `/clients/{cid}?tab=stakeholders&openContact=<id>`
                doorway so we never build a parallel contact viewer. */}
            {topContacts.length > 0 && (
              <div className="mt-4" data-testid="embrace-top-contacts">
                <div className="text-[0.5938rem] font-mono uppercase tracking-[0.2em] text-eyebrow mb-2">
                  Relationship anchors
                </div>
                <div className="flex flex-col gap-1.5">
                  {topContacts.map((c) => (
                    <Link
                      key={c.id}
                      to={`/clients/${client.id}?tab=stakeholders&openContact=${c.id}`}
                      className="flex items-center gap-2 group"
                      data-testid={`embrace-top-contact-${c.id}`}
                      title={`${c.name}${c.title ? ` · ${c.title}` : ""}${c.posture && c.posture !== "unknown" ? ` · ${c.posture}` : ""}`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 border font-mono text-[0.5938rem] font-bold shrink-0 ${
                          c.influence === "high"
                            ? "border-primary text-primary bg-primary/10"
                            : "border-brass/60 text-brass bg-brass/10"
                        }`}
                        aria-hidden
                      >
                        {contactInitials(c.name)}
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-foreground truncate group-hover:text-primary transition-colors">
                          {c.name}
                          {c.is_primary && (
                            <span
                              className="ml-1.5 text-[0.5625rem] font-mono uppercase tracking-widest text-brass"
                              aria-label="Primary contact"
                            >
                              Primary
                            </span>
                          )}
                        </div>
                        {c.title && (
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate">
                            {c.title}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Atmosphere · right — image OR Yadid texture fallback.
            The frame height is locked (min-h: 180/220) so the presence
            or absence of a headline image never changes the masthead
            layout dimensions. */}
        <div
          ref={frameRef}
          onPointerDown={onFramePointerDown}
          onPointerMove={onFramePointerMove}
          onPointerUp={onFramePointerUp}
          onPointerCancel={onFramePointerUp}
          className={`relative overflow-hidden border border-border/60 min-h-[180px] md:min-h-[220px] group ${
            repositioning ? "cursor-grabbing ring-2 ring-brass" : ""
          }`}
          style={{ touchAction: repositioning ? "none" : "auto" }}
          data-testid={heroIsEngagement ? "embrace-cover-headline" : (hero ? "embrace-cover-image" : "embrace-cover-texture")}
        >
          {/* Hidden file input drives the upload from any of the inline
              affordances below. Kept out of the presentation so it does
              not compete with the atmospheric visual. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => uploadHeadline(e.target.files?.[0])}
            data-testid="embrace-cover-headline-input"
          />
          {hero ? (
            <img
              src={hero}
              alt=""
              className="absolute inset-0 h-full w-full object-cover motion-reduce:transition-none select-none"
              style={{ objectPosition: `${heroPos.x}% ${heroPos.y}%` }}
              loading="lazy"
              draggable={false}
              data-testid="embrace-cover-headline-img"
            />
          ) : (
            /* Yadid texture fallback — restrained grain built from CSS so
               no image asset is required and dark mode renders correctly.
               Ivory in light mode, Navy Ink in dark mode. */
            <div
              className="absolute inset-0 bg-secondary/40"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
                backgroundSize: "12px 12px, 12px 12px",
                backgroundPosition: "0 0, 6px 6px",
              }}
              aria-hidden
            />
          )}
          {/* Uniform dark shade over the image so it sits back and lets
              the framing caption read cleanly. Kept as a very-gentle
              gradient (a hair heavier at the base) rather than a hard
              flat overlay so the image doesn't feel like it's been
              painted over — it just quietens down. `pointer-events-none`
              so the reposition drag still reaches the image. */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/55 pointer-events-none" aria-hidden />

          {/* Unobtrusive edit affordances · appear only on hover / focus
              so they don't compete with the presentation.
              Iter 62 · Bumped `z-10` above the caption block so clicks
              actually reach these buttons — previously the caption's
              `relative h-full` wrapper (later in DOM) stole every
              click on this affordance.
              Iter 64 · When `repositioning` is true the strip stays
              visible (opacity-100) and swaps to Save / Cancel so the
              advisor can commit or discard the drag. */}
          <div
            className={`absolute top-2 right-2 z-10 flex items-center gap-1 transition-opacity ${
              repositioning ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
            }`}
            data-testid="embrace-cover-headline-actions"
          >
            {repositioning ? (
              <>
                <span className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/80 bg-black/50 px-2 py-1 backdrop-blur-sm pointer-events-none">
                  Drag to reframe
                </span>
                <button
                  type="button"
                  onClick={saveReposition}
                  disabled={savingPos}
                  className="text-[0.5938rem] font-mono uppercase tracking-widest text-white bg-brass/90 hover:bg-brass px-2 py-1 backdrop-blur-sm disabled:opacity-60"
                  data-testid="embrace-cover-headline-save-position"
                >
                  {savingPos ? "Saving…" : "Save framing"}
                </button>
                <button
                  type="button"
                  onClick={cancelReposition}
                  className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/90 bg-black/45 hover:bg-black/70 px-2 py-1 backdrop-blur-sm"
                  data-testid="embrace-cover-headline-cancel-position"
                >
                  Cancel
                </button>
              </>
            ) : heroIsEngagement ? (
              <>
                <button
                  type="button"
                  onClick={() => setRepositioning(true)}
                  className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/90 bg-black/45 hover:bg-black/70 px-2 py-1 backdrop-blur-sm"
                  data-testid="embrace-cover-headline-reposition"
                >
                  Reposition
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/90 bg-black/45 hover:bg-black/70 px-2 py-1 backdrop-blur-sm"
                  data-testid="embrace-cover-headline-change"
                >
                  {uploading ? "Uploading…" : "Change image"}
                </button>
                <button
                  type="button"
                  onClick={removeHeadline}
                  className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/90 bg-black/45 hover:bg-red-900/80 px-2 py-1 backdrop-blur-sm"
                  data-testid="embrace-cover-headline-remove"
                >
                  Remove
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-[0.5938rem] font-mono uppercase tracking-widest text-white/90 bg-black/45 hover:bg-black/70 px-2 py-1 backdrop-blur-sm"
                data-testid="embrace-cover-headline-add"
              >
                {uploading ? "Uploading…" : "Add headline image"}
              </button>
            )}
          </div>

          {/* Framing caption block · `pointer-events-none` on the
              full-height wrapper so clicks pass through to the
              actions affordance above. Iter 84 · The ornamental
              "Atmosphere · Yadid texture / Client-supplied / Engagement
              headline" caption line is removed — the framing quote
              alone carries the atmospheric role. */}
          <div className="relative h-full flex flex-col justify-end p-5 md:p-6 pointer-events-none">
            <div
              className="text-white/95 max-w-xl"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.1rem", lineHeight: 1.3, fontStyle: "italic", textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
              data-testid="embrace-cover-framing"
            >
              “{framingQuestion}”
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
